import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config/env';
import './login.css';

type AuthUser = {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string | null;
  role?: string;
  roles?: string[];
  platformAdmin?: boolean;
};

type StoredSession = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser | null;
  expiresAt: number;
  remember: boolean;
};

type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  user: AuthUser | undefined;
  getAccessTokenSilently: (_options?: Record<string, unknown>) => Promise<string>;
  loginWithRedirect: (options?: Record<string, unknown> & { appState?: { returnTo?: string } }) => Promise<void>;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  loginWithPassword: (
    email: string,
    password: string,
    options?: { remember?: boolean; language?: LoginLanguage },
  ) => Promise<{ mustChangePassword: boolean; isPlatformAdmin: boolean }>;
};

const STORAGE_KEY = 'neoemr.admin.auth';
const LOGIN_PATH = '/login';
const DEFAULT_RETURN_TO = '/dashboard';
const TOKEN_REFRESH_SKEW_MS = 45_000;
const LOGIN_LANGUAGE_QUERY_PARAM = 'language';

type LoginLanguage = 'en' | 'es' | 'it';
type AuthLoginVariant = 'staff' | 'admin' | 'portal';

type LoginCopy = {
  contextLabel: string;
  panelKicker: string;
  headline: string;
  subheadline: string;
  emailLabel: string;
  passwordLabel: string;
  forgotPassword: string;
  rememberDevice: string;
  submit: string;
  submitting: string;
  supportCopy: string;
  privacyPolicy: string;
  securityProtocol: string;
  systemStatus: string;
  heroLead: string;
  heroMiddle: string;
  heroAccent: string;
  genericError: string;
};

const HOST_DEFAULT_LANGUAGE_MAP: Record<string, LoginLanguage> = {
  'app.elen1.com': 'en',
  'mi.elen1.com': 'es',
  'it.elen1.com': 'it',
};

const LOGIN_COPY: Record<AuthLoginVariant, Record<LoginLanguage, LoginCopy>> = {
  staff: {
    en: {
      contextLabel: 'Elen1 EMR',
      panelKicker: 'System verification',
      headline: 'Welcome Back',
      subheadline: 'Enter your credentials to access the workspace.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot password?',
      rememberDevice: 'Remember device for 30 days',
      submit: 'Sign in',
      submitting: 'Signing in...',
      supportCopy: 'Need access ? Contact your organization administrator.',
      privacyPolicy: 'Privacy policy',
      securityProtocol: 'Security protocol',
      systemStatus: 'ACTIVE',
      heroLead: 'A Modern',
      heroMiddle: 'Clinical Intelligence',
      heroAccent: 'Platform.',
      genericError: 'Unable to sign in.',
    },
    es: {
      contextLabel: 'Elen1 EMR',
      panelKicker: 'Verificacion del sistema',
      headline: 'Bienvenido de nuevo',
      subheadline: 'Ingrese sus credenciales para acceder al espacio de trabajo.',
      emailLabel: 'Correo electronico',
      passwordLabel: 'Contrasena',
      forgotPassword: 'Olvido su contrasena?',
      rememberDevice: 'Recordar este dispositivo durante 30 dias',
      submit: 'Iniciar sesion',
      submitting: 'Iniciando sesion...',
      supportCopy: 'Necesita acceso? Contacte al administrador de su organizacion.',
      privacyPolicy: 'Politica de privacidad',
      securityProtocol: 'Protocolo de seguridad',
      systemStatus: 'ACTIVE',
      heroLead: 'Una plataforma',
      heroMiddle: 'moderna de inteligencia',
      heroAccent: 'clinica.',
      genericError: 'No se pudo iniciar sesion.',
    },
    it: {
      contextLabel: 'Elen1 EMR',
      panelKicker: 'Verifica di sistema',
      headline: 'Bentornato',
      subheadline: "Inserisci le tue credenziali per accedere all'area di lavoro.",
      emailLabel: 'Indirizzo email',
      passwordLabel: 'Password',
      forgotPassword: 'Password dimenticata?',
      rememberDevice: 'Ricorda questo dispositivo per 30 giorni',
      submit: 'Accedi',
      submitting: 'Accesso in corso...',
      supportCopy: "Hai bisogno di accesso? Contatta l'amministratore della tua organizzazione.",
      privacyPolicy: 'Informativa sulla privacy',
      securityProtocol: 'Protocollo di sicurezza',
      systemStatus: 'ACTIVE',
      heroLead: 'Una piattaforma',
      heroMiddle: 'moderna di intelligenza',
      heroAccent: 'clinica.',
      genericError: 'Impossibile accedere.',
    },
  },
  admin: {
    en: {
      contextLabel: 'Elen1 Admin',
      panelKicker: 'System verification',
      headline: 'Welcome Back',
      subheadline: 'Enter your administrator credentials to access the control plane.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot password?',
      rememberDevice: 'Remember device for 30 days',
      submit: 'Sign in',
      submitting: 'Signing in...',
      supportCopy: 'Need access ? Contact your organization administrator.',
      privacyPolicy: 'Privacy policy',
      securityProtocol: 'Security protocol',
      systemStatus: 'ACTIVE',
      heroLead: 'A Modern',
      heroMiddle: 'Clinical Intelligence',
      heroAccent: 'Platform.',
      genericError: 'Unable to sign in.',
    },
    es: {
      contextLabel: 'Elen1 Admin',
      panelKicker: 'Verificacion del sistema',
      headline: 'Bienvenido de nuevo',
      subheadline: 'Ingrese sus credenciales de administrador para acceder al panel de control.',
      emailLabel: 'Correo electronico',
      passwordLabel: 'Contrasena',
      forgotPassword: 'Olvido su contrasena?',
      rememberDevice: 'Recordar este dispositivo durante 30 dias',
      submit: 'Iniciar sesion',
      submitting: 'Iniciando sesion...',
      supportCopy: 'Necesita acceso? Contacte al administrador de su organizacion.',
      privacyPolicy: 'Politica de privacidad',
      securityProtocol: 'Protocolo de seguridad',
      systemStatus: 'ACTIVE',
      heroLead: 'Una plataforma',
      heroMiddle: 'moderna de inteligencia',
      heroAccent: 'clinica.',
      genericError: 'No se pudo iniciar sesion.',
    },
    it: {
      contextLabel: 'Elen1 Admin',
      panelKicker: 'Verifica di sistema',
      headline: 'Bentornato',
      subheadline: 'Inserisci le credenziali di amministratore per accedere al pannello di controllo.',
      emailLabel: 'Indirizzo email',
      passwordLabel: 'Password',
      forgotPassword: 'Password dimenticata?',
      rememberDevice: 'Ricorda questo dispositivo per 30 giorni',
      submit: 'Accedi',
      submitting: 'Accesso in corso...',
      supportCopy: "Hai bisogno di accesso? Contatta l'amministratore della tua organizzazione.",
      privacyPolicy: 'Informativa sulla privacy',
      securityProtocol: 'Protocollo di sicurezza',
      systemStatus: 'ACTIVE',
      heroLead: 'Una piattaforma',
      heroMiddle: 'moderna di intelligenza',
      heroAccent: 'clinica.',
      genericError: 'Impossibile accedere.',
    },
  },
  portal: {
    en: {
      contextLabel: 'Elen1 Patient Portal',
      panelKicker: 'System verification',
      headline: 'Welcome Back',
      subheadline: 'Enter your patient portal credentials to continue.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot password?',
      rememberDevice: 'Remember device for 30 days',
      submit: 'Sign in',
      submitting: 'Signing in...',
      supportCopy: 'Need a reset or portal access? Contact your clinic directly.',
      privacyPolicy: 'Privacy policy',
      securityProtocol: 'Security protocol',
      systemStatus: 'ACTIVE',
      heroLead: 'A Modern',
      heroMiddle: 'Clinical Intelligence',
      heroAccent: 'Platform.',
      genericError: 'Unable to sign in.',
    },
    es: {
      contextLabel: 'Elen1 Patient Portal',
      panelKicker: 'Verificacion del sistema',
      headline: 'Bienvenido de nuevo',
      subheadline: 'Ingrese sus credenciales del portal del paciente para continuar.',
      emailLabel: 'Correo electronico',
      passwordLabel: 'Contrasena',
      forgotPassword: 'Olvido su contrasena?',
      rememberDevice: 'Recordar este dispositivo durante 30 dias',
      submit: 'Iniciar sesion',
      submitting: 'Iniciando sesion...',
      supportCopy: 'Necesita un restablecimiento o acceso al portal? Contacte directamente a su clinica.',
      privacyPolicy: 'Politica de privacidad',
      securityProtocol: 'Protocolo de seguridad',
      systemStatus: 'ACTIVE',
      heroLead: 'Una plataforma',
      heroMiddle: 'moderna de inteligencia',
      heroAccent: 'clinica.',
      genericError: 'No se pudo iniciar sesion.',
    },
    it: {
      contextLabel: 'Elen1 Patient Portal',
      panelKicker: 'Verifica di sistema',
      headline: 'Bentornato',
      subheadline: 'Inserisci le credenziali del portale paziente per continuare.',
      emailLabel: 'Indirizzo email',
      passwordLabel: 'Password',
      forgotPassword: 'Password dimenticata?',
      rememberDevice: 'Ricorda questo dispositivo per 30 giorni',
      submit: 'Accedi',
      submitting: 'Accesso in corso...',
      supportCopy: 'Hai bisogno di un reset o di accesso al portale? Contatta direttamente la tua clinica.',
      privacyPolicy: 'Informativa sulla privacy',
      securityProtocol: 'Protocollo di sicurezza',
      systemStatus: 'ACTIVE',
      heroLead: 'Una piattaforma',
      heroMiddle: 'moderna di intelligenza',
      heroAccent: 'clinica.',
      genericError: 'Impossibile accedere.',
    },
  },
};

const AuthContext = createContext<AuthContextValue | null>(null);

function createLoginRequiredError() {
  const error = new Error('Login required') as Error & { error?: string };
  error.error = 'login_required';
  return error;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length < 2) return {};
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function buildUserFromPayload(payload: Record<string, unknown>, currentUser?: AuthUser | null): AuthUser {
  const givenName = typeof payload.given_name === 'string' ? payload.given_name : currentUser?.given_name;
  const familyName = typeof payload.family_name === 'string' ? payload.family_name : currentUser?.family_name;
  const fullName =
    (typeof payload.name === 'string' && payload.name.trim()) ||
    [givenName, familyName].filter(Boolean).join(' ') ||
    currentUser?.name ||
    undefined;

  return {
    sub: typeof payload.sub === 'string' ? payload.sub : currentUser?.sub,
    email: typeof payload.email === 'string' ? payload.email : currentUser?.email,
    name: fullName,
    given_name: givenName,
    family_name: familyName,
    picture: typeof payload.picture === 'string' ? payload.picture : currentUser?.picture ?? null,
    role: typeof payload.role === 'string' ? payload.role : currentUser?.role,
    roles: Array.isArray(payload.roles)
      ? payload.roles.map((entry) => String(entry))
      : currentUser?.roles,
    platformAdmin: payload.platformAdmin === true || currentUser?.platformAdmin === true,
  };
}

function deriveExpiry(token: string) {
  const payload = decodeJwtPayload(token);
  const exp = typeof payload.exp === 'number' ? payload.exp : Number(payload.exp || 0);
  if (!Number.isFinite(exp) || exp <= 0) {
    return Date.now() + 15 * 60 * 1000;
  }
  return exp * 1000;
}

function loadStoredSession() {
  const raw = window.localStorage.getItem(STORAGE_KEY) || window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed?.accessToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearStoredSession() {
  window.localStorage.removeItem(STORAGE_KEY);
  window.sessionStorage.removeItem(STORAGE_KEY);
}

function persistSession(session: StoredSession) {
  clearStoredSession();
  const serialized = JSON.stringify(session);
  if (session.remember) {
    window.localStorage.setItem(STORAGE_KEY, serialized);
  } else {
    window.sessionStorage.setItem(STORAGE_KEY, serialized);
  }
}

async function readResponseMessage(response: Response, fallback: string) {
  const text = await response.text().catch(() => '');
  if (!text.trim()) return fallback;
  try {
    const parsed = JSON.parse(text) as { message?: string; error?: string };
    return parsed.message || parsed.error || text;
  } catch {
    return text;
  }
}

function normalizeLoginLanguage(value?: unknown): LoginLanguage | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('it')) return 'it';
  if (normalized.startsWith('en')) return 'en';
  return null;
}

function inferLanguageFromPath(path?: string | null): LoginLanguage | null {
  if (typeof path !== 'string') return null;
  const normalized = path.trim().toLowerCase();
  if (!normalized) return null;
  if (/^\/(?:es|es-co|es-es|es-mx)(?:\/|$)/.test(normalized)) return 'es';
  if (/^\/(?:it|it-it)(?:\/|$)/.test(normalized)) return 'it';
  if (/^\/en(?:\/|$)/.test(normalized)) return 'en';
  return null;
}

function resolveHostDefaultLanguage(): LoginLanguage | null {
  if (typeof window === 'undefined') return null;
  return HOST_DEFAULT_LANGUAGE_MAP[window.location.hostname] ?? null;
}

function resolveReferrerLanguage(): LoginLanguage | null {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    const referrer = new URL(document.referrer);
    return inferLanguageFromPath(referrer.pathname) || HOST_DEFAULT_LANGUAGE_MAP[referrer.hostname] || null;
  } catch {
    return null;
  }
}

function resolveCurrentLoginLanguage(path?: string | null): LoginLanguage {
  return (
    inferLanguageFromPath(path) ||
    resolveReferrerLanguage() ||
    resolveHostDefaultLanguage() ||
    (typeof document !== 'undefined' ? normalizeLoginLanguage(document.documentElement.lang) : null) ||
    (typeof navigator !== 'undefined' ? normalizeLoginLanguage(navigator.language) : null) ||
    'en'
  );
}

function resolveLoginLanguage(searchParams: URLSearchParams): LoginLanguage {
  return (
    normalizeLoginLanguage(
      searchParams.get(LOGIN_LANGUAGE_QUERY_PARAM) ||
        searchParams.get('lang') ||
        searchParams.get('locale'),
    ) ||
    inferLanguageFromPath(searchParams.get('returnTo')) ||
    resolveCurrentLoginLanguage()
  );
}

async function fetchCurrentUser(accessToken: string, currentUser?: AuthUser | null): Promise<AuthUser> {
  const payload = decodeJwtPayload(accessToken);
  if (payload.platformAdmin === true) {
    return buildUserFromPayload(payload, currentUser);
  }

  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(await readResponseMessage(response, 'Unable to load session.'));
  }

  const profile = (await response.json()) as {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    roles: string[];
  };

  return {
    sub: profile.id,
    email: profile.email,
    given_name: profile.firstName,
    family_name: profile.lastName,
    name: `${profile.firstName} ${profile.lastName}`.trim(),
    picture: null,
    role: profile.role,
    roles: profile.roles,
    platformAdmin: false,
  };
}

async function refreshSession(current: StoredSession): Promise<StoredSession> {
  if (!current.refreshToken) {
    throw createLoginRequiredError();
  }

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refreshToken: current.refreshToken,
    }),
  });

  if (!response.ok) {
    throw createLoginRequiredError();
  }

  const payload = (await response.json()) as { accessToken: string };
  const user = await fetchCurrentUser(payload.accessToken, current.user);
  const nextSession: StoredSession = {
    ...current,
    accessToken: payload.accessToken,
    user,
    expiresAt: deriveExpiry(payload.accessToken),
  };
  persistSession(nextSession);
  return nextSession;
}

function buildLoginUrl(returnTo?: string, language?: LoginLanguage) {
  const params = new URLSearchParams();
  if (returnTo && returnTo.trim()) {
    params.set('returnTo', returnTo);
  }
  const resolvedLanguage = language || resolveCurrentLoginLanguage(returnTo);
  if (resolvedLanguage !== 'en') {
    params.set(LOGIN_LANGUAGE_QUERY_PARAM, resolvedLanguage);
  }
  const query = params.toString();
  return query ? `${LOGIN_PATH}?${query}` : LOGIN_PATH;
}

export function Auth0Provider({
  children,
}: {
  children: React.ReactNode;
  [key: string]: unknown;
}) {
  const [session, setSession] = useState<StoredSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const refreshPromiseRef = useRef<Promise<StoredSession> | null>(null);

  useEffect(() => {
    let active = true;
    const restore = async () => {
      const stored = loadStoredSession();
      if (!stored) {
        if (active) setIsLoading(false);
        return;
      }

      try {
        const stillValid = stored.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS;
        const nextSession = stillValid ? stored : await refreshSession(stored);
        if (!active) return;
        setSession(nextSession);
        setError(null);
      } catch (restoreError) {
        clearStoredSession();
        const nextError =
          restoreError instanceof Error && (restoreError as Error & { error?: string }).error === 'login_required'
            ? null
            : restoreError instanceof Error
              ? restoreError
              : new Error('Unable to restore session.');
        if (!active) return;
        setSession(null);
        setError(nextError);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    restore().catch(() => {
      if (!active) return;
      setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const getAccessTokenSilently = async () => {
    if (!session) {
      throw createLoginRequiredError();
    }

    if (session.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS) {
      return session.accessToken;
    }

    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = refreshSession(session).finally(() => {
        refreshPromiseRef.current = null;
      });
    }

    try {
      const nextSession = await refreshPromiseRef.current;
      setSession(nextSession);
      setError(null);
      return nextSession.accessToken;
    } catch {
      clearStoredSession();
      setSession(null);
      setError(null);
      throw createLoginRequiredError();
    }
  };

  const loginWithRedirect = async (options?: Record<string, unknown> & { appState?: { returnTo?: string } }) => {
    const returnTo =
      options?.appState?.returnTo ||
      `${window.location.pathname}${window.location.search}${window.location.hash}` ||
      DEFAULT_RETURN_TO;
    window.location.assign(buildLoginUrl(returnTo, resolveCurrentLoginLanguage(returnTo)));
  };

  const logout = (options?: { logoutParams?: { returnTo?: string } }) => {
    clearStoredSession();
    setSession(null);
    setError(null);
    const returnTo =
      options?.logoutParams?.returnTo ||
      buildLoginUrl(DEFAULT_RETURN_TO, resolveCurrentLoginLanguage(DEFAULT_RETURN_TO));
    window.location.assign(returnTo);
  };

  const loginWithPassword = async (
    email: string,
    password: string,
    options?: { remember?: boolean; language?: LoginLanguage },
  ) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error(await readResponseMessage(response, LOGIN_COPY.staff[options?.language ?? 'en'].genericError));
    }

    const payload = (await response.json()) as {
      accessToken: string;
      refreshToken: string;
      mustChangePassword: boolean;
      isPlatformAdmin?: boolean;
    };

    const user = await fetchCurrentUser(payload.accessToken);
    const nextSession: StoredSession = {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      user,
      expiresAt: deriveExpiry(payload.accessToken),
      remember: options?.remember ?? true,
    };

    persistSession(nextSession);
    setSession(nextSession);
    setError(null);

    return {
      mustChangePassword: payload.mustChangePassword,
      isPlatformAdmin: payload.isPlatformAdmin === true,
    };
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(session?.accessToken),
      isLoading,
      error,
      user: session?.user ?? undefined,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      loginWithPassword,
    }),
    [error, isLoading, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth0() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth0 must be used within Auth0Provider.');
  }
  return context;
}

function HelpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M9.7 9.2a2.54 2.54 0 0 1 4.98.78c0 1.72-1.74 2.14-2.44 3.06"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="16.85" r="1.05" fill="currentColor" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="m12 3.3 1.42 1.08 1.82-.28.92 1.58 1.74.64-.1 1.79 1.19 1.34-1.02 1.5 1.02 1.5-1.19 1.34.1 1.79-1.74.64-.92 1.58-1.82-.28L12 20.7l-1.42-1.08-1.82.28-.92-1.58-1.74-.64.1-1.79-1.19-1.34 1.02-1.5-1.02-1.5 1.19-1.34-.1-1.79 1.74-.64.92-1.58 1.82.28L12 3.3Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
      <circle cx="12" cy="12" r="2.7" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

type AuthLoginPageProps = {
  variant?: AuthLoginVariant;
  contextLabel?: string;
  headline?: string;
  subheadline?: string;
  supportCopy?: string;
};

export function AuthLoginPage({
  variant = 'staff',
  contextLabel,
  headline,
  subheadline,
  supportCopy,
}: AuthLoginPageProps) {
  const { isAuthenticated, loginWithPassword } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const returnTo = searchParams.get('returnTo') || DEFAULT_RETURN_TO;
  const loginLanguage = useMemo(() => resolveLoginLanguage(searchParams), [searchParams]);
  const copy = LOGIN_COPY[variant][loginLanguage];
  const resolvedContextLabel = contextLabel ?? copy.contextLabel;
  const resolvedHeadline = headline ?? copy.headline;
  const resolvedSubheadline = subheadline ?? copy.subheadline;
  const resolvedSupportCopy = supportCopy ?? copy.supportCopy;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', loginLanguage);
  }, [loginLanguage]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await loginWithPassword(email, password, { remember, language: loginLanguage });
      navigate(returnTo, { replace: true });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : copy.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-topbar">
          <div className="auth-wordmark" title={resolvedContextLabel}>
            elen1
          </div>
          <div className="auth-tools" aria-hidden="true">
            <span className="auth-tool">
              <HelpIcon />
            </span>
            <span className="auth-tool">
              <SettingsIcon />
            </span>
          </div>
        </div>

        <section className="auth-hero">
          <div className="auth-hero-veil auth-hero-veil-one" />
          <div className="auth-hero-veil auth-hero-veil-two" />

          <div className="auth-hero-copy">
            <h1>
              {copy.heroLead}
              <br />
              {copy.heroMiddle}
              <br />
              <span>{copy.heroAccent}</span>
            </h1>
          </div>

          <p className="auth-hero-footer">© 2026 ELEN1 SYSTEMS. ALL RIGHTS RESERVED.</p>
        </section>

        <section className="auth-panel">
          <div className="auth-form-shell" data-context-label={resolvedContextLabel}>
            <p className="auth-panel-kicker">{copy.panelKicker}</p>
            <h2 className="auth-panel-title">{resolvedHeadline}</h2>
            <p className="auth-panel-copy">{resolvedSubheadline}</p>

            <form className="auth-form" onSubmit={onSubmit}>
              <label className="auth-field">
                <span className="auth-field-label">{copy.emailLabel}</span>
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="doctor@elen1.com"
                  required
                />
              </label>

              <label className="auth-field">
                <span className="auth-field-heading">
                  <span className="auth-field-label">{copy.passwordLabel}</span>
                  <span className="auth-field-meta">{copy.forgotPassword}</span>
                </span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••••••"
                  required
                />
              </label>

              <label className="auth-checkbox">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={() => setRemember((current) => !current)}
                />
                <span className="auth-checkbox-box" />
                <span>{copy.rememberDevice}</span>
              </label>

              {error ? <div className="auth-error">{error}</div> : null}

              <button type="submit" className="auth-submit" disabled={submitting}>
                {submitting ? copy.submitting : copy.submit}
              </button>
            </form>

            <div className="auth-panel-divider" />
            <p className="auth-panel-support">{resolvedSupportCopy}</p>
          </div>

          <div className="auth-panel-footer">
            <span>{copy.privacyPolicy}</span>
            <span>{copy.securityProtocol}</span>
            <span className="auth-system-status">
              <span className="auth-system-status-dot" />
              {copy.systemStatus}
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
